import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ShipmentInventory, ShipmentInventoryAddPayload, shipmentInventoryApi, ShipmentInventoryResponse, ShipmentInventoryVerifyPayload } from "../apis/inventoryApis";
import z from "zod";

export const ShipmentInventoryAddPayloadSchema = z.object({
  barcodeId: z.string().min(1, "Shelf ID is required"),
  orderId: z.array(
    z.string().min(1, "AWB number is required")
  ).min(1, "At least one AWB is required"),
});

const UpdateShipmentInventoryPayloadSchema = z.object({
    locationId: z.string().min(1, "Shelf ID is required"),
    awb: z.string().min(1, "AWB number is required"),
});

export const useShipmentInventoryListData = (pageNo: number, limit:number, searchFor: string) => {
    return useQuery({
        queryKey: ["shipmentInventoryList", pageNo,limit, searchFor],
        queryFn: async () => {
            const res = await shipmentInventoryApi.getShipmentInventory(pageNo,limit, searchFor);
            if (!res.success) {
                throw new Error(res.message || "Failed to fetch shipment inventory");
            }
            return res.data;
        },
        staleTime: 1000 * 60 * 5, // 5 minutes
    });
};

export const useFetchShipmentInventoryDataByLocation = (locatoinId: string, pageNo: number, limit:number, searchFor: string) => {
    return useQuery({
        queryKey: ["shipmentInventoryList", locatoinId, pageNo,limit, searchFor],
        queryFn: async () => {
            if (!locatoinId) {
                return;
            }
            const res = await shipmentInventoryApi.getShipmentInventoryByLocationId(locatoinId, pageNo,limit, searchFor);
            if (!res.success) {
                throw new Error(res.message || "Failed to fetch shipment inventory");
            }
            return res.data;
        },
        enabled: !!locatoinId,
        staleTime: 1000 * 60 * 5, // 5 minutes
    });
};

const useAddShipmentInverntoryMutation = <T, P>(
    mutationFn: (payload: P, isFormData?: boolean) => Promise<any>,
    successMessage: string,
    invalidateKeys: string[] = [],
    validator?: z.ZodSchema<P>,
) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload: P) => {
            if (validator) {
                validator.parse(payload);
            }
            const res = await mutationFn(payload);
            if (!res.success) {
                throw new Error(res.message || `${successMessage} failed`);
            }
            return res.data;
        },
        onSuccess: (data: T) => {
            console.log(`✅ ${successMessage}:`, data);
            invalidateKeys.forEach((key) =>
                queryClient.invalidateQueries({ queryKey: [key] }),
            );
        },
        onError: (error: Error) => {
            console.error(`❌ ${successMessage} failed:`, error.message);
        },
    });
};

export const useVerifyLocationIdBasedMutation = <T,P>(
  mutationFn: (payload:P,isFormData?: boolean ) => Promise<any>,
  successMessage: string,
  invalidateKeys: string[] = [],
  validator?: z.ZodSchema<P>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload:P) => {
      // ✅ Validate
      if (validator) validator.parse(payload);

    //   if (!payload?.locationId) {
    //     throw new Error("Location ID is required");
    //   }
    //   if (!payload?.awb) {
    //     throw new Error("AWB is required");
    //   }

      // ✅ Call your API
      const res = await mutationFn(payload);

      if (!res?.success) {
        throw new Error(res?.message || `${successMessage} failed`);
      }

      return res.data as T;
    },

    onSuccess: (data) => {
      console.log(`✅ ${successMessage}:`, data);
      invalidateKeys.forEach((key) =>
        queryClient.invalidateQueries({ queryKey: [key] })
      );
    },

    onError: (error) => {
      console.error(`❌ ${successMessage} failed:`, error.message);
    },
  });
};


export const useShipmentInventoryAddMutation = () =>
    useAddShipmentInverntoryMutation<ShipmentInventory, ShipmentInventoryAddPayload>(
        shipmentInventoryApi.shipmentInventoryAdd,
        "Shipment Inventory Added successfully",
        ["shipmentInventoryAdd", "shipmentInventoryAddedData"],
        ShipmentInventoryAddPayloadSchema,
    );

export const useShipmentInventoryVerifyMutation = () =>
    useVerifyLocationIdBasedMutation<ShipmentInventory, ShipmentInventoryVerifyPayload>(
        shipmentInventoryApi.verifyShipmentInventory,
        "Shipment Inventory verify successfully",
        ["shipmentInventoryList"],
        UpdateShipmentInventoryPayloadSchema,
    );

export const useShipmentInventoryDeleteMutation = () =>
    useVerifyLocationIdBasedMutation<void, ShipmentInventoryVerifyPayload>(
        shipmentInventoryApi.deleteShipmentInventory,
        "Shipment Inventory deleted successfully",
        ["shipmentInventoryList"],
        undefined,
    );

export const useLocationBarcodeVerifyMutation = () =>
    useVerifyLocationIdBasedMutation<ShipmentInventoryResponse, ShipmentInventoryVerifyPayload>(
        shipmentInventoryApi.verifyLocationBarcode,
        "Barcode location verify successfully",
        ["shipmentInventoryList"],
        undefined,
    );